import type { ChargerHealth, ChargerTileRing, ChargerTileState } from "../types/charger";

/** Stable 32-bit hash from charger id — same mock across refreshes. */
function hashId(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function pick(hash: number, slot: number, threshold: number): boolean {
  return ((hash >>> slot) & 0xff) < threshold;
}

function healthFromHash(hash: number): ChargerHealth {
  if (pick(hash, 0, 26)) return "stale";
  if (pick(hash, 1, 64)) return "unknown";
  return "ok";
}

function ringFromState(evConnected: boolean, charging: boolean): ChargerTileRing {
  if (charging) return "blue";
  if (evConnected) return "green";
  return "none";
}

export function normalizeChargerLabel(id: string): string {
  return id.replace(/^\//, "").trim();
}

/** Permanent legend/reference tile — all segment-bar indicators on, health OK. */
export const TEST_CHARGER_TILE: ChargerTileState = {
  id: "__test__",
  label: "TEST",
  health: "ok",
  evConnected: false,
  charging: false,
  mv5: true,
  fcrActivated: true,
  sp: true,
  resp: true,
  ring: "none",
  powerGauge: { current: 10, max: 11 },
};

export function serverHealthToTileHealth(
  health: Record<string, "ok" | "new" | "stale"> | undefined,
  id: string,
): ChargerHealth | undefined {
  if (!health) return undefined;
  const label = normalizeChargerLabel(id);
  const value = health[label] ?? health[id];
  if (value === "ok") return "ok";
  if (value === "new") return "unknown";
  if (value === "stale") return "stale";
  return undefined;
}

export function mockChargerState(
  id: string,
  healthOverride?: ChargerHealth,
): ChargerTileState {
  const label = normalizeChargerLabel(id);
  const hash = hashId(label);

  const evConnected = false;
  const charging = false;

  return {
    id,
    label,
    health: healthOverride ?? healthFromHash(hash),
    evConnected,
    charging,
    mv5: false,
    fcrActivated: false,
    sp: false,
    resp: false,
    ring: ringFromState(evConnected, charging),
  };
}

export function mockChargerStates(
  ids: string[],
  health?: Record<string, "ok" | "new" | "stale">,
): ChargerTileState[] {
  return ids.map((id) =>
    mockChargerState(id, serverHealthToTileHealth(health, id)),
  );
}
